import React from 'react';
const { useState, useEffect } = React;
import { METHOD_INDUSTRIES, METHOD_TEAM_SIZES, METHOD_SALE_TYPES, METHOD_COUNTRY_LABELS, labelOfIn } from '../../methodLibraryConfig.js';
import { adminCall, cardSt, labelSt, mutedSt, inputSt, warnSt, fmtDate } from './shared.jsx';

// "Contexto del cliente": the four values the Method Library is filtered by
// (industry, team size, sale type, country). The code resolves them from the
// account, the intake, the evaluated people, the CRM and the self-assessments;
// Ale confirms or changes each one. P3, P5 and System Design do not run until
// the four are known. A changed value goes back to the computed one with "Calculado".

const FIELDS = [
  { key: 'industry', label: 'Industria', options: METHOD_INDUSTRIES },
  { key: 'teamSize', label: 'Tamaño del equipo comercial', options: METHOD_TEAM_SIZES },
  { key: 'saleType', label: 'Tipo de venta', options: METHOD_SALE_TYPES },
  { key: 'country', label: 'País', options: Object.entries(METHOD_COUNTRY_LABELS).filter(([k]) => !['latam', 'europe', 'northAmerica'].includes(k)).map(([key, label]) => ({ key, label })) },
];
const SOURCES = {
  account: 'de la cuenta', people: 'de las personas evaluadas', intake: 'del intake', crm: 'del ciclo en el CRM', self: 'de las autoevaluaciones',
  quoteMarket: 'del mercado de la cotización', ale: 'elegido por ti',
};

export default function ClientContextCard({ diagnosticId, run, busy, onChange }) {
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState('');
  const load = () => run(async () => { const c = await adminCall({ action: 'clientContext', diagnosticId }); setCtx(c); onChange?.(c); });
  useEffect(() => { load(); }, [diagnosticId]);

  // No key: only confirms what is shown. With a key: changes it (empty = back to the computed value)
  const save = (key, value) => run(async () => {
    setError('');
    try { const c = await adminCall({ action: 'saveClientContext', diagnosticId, ...(key ? { [key]: value || null } : {}) }); setCtx(c); onChange?.(c); }
    catch (e) { setError(e.message); }
  });
  if (!ctx) return null;

  return (
    <div style={ctx.complete ? cardSt : warnSt}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={labelSt}>Contexto del cliente</div>
        {ctx.confirmedAt && <span style={mutedSt}>Confirmado por {ctx.confirmedBy || '—'} el {fmtDate(ctx.confirmedAt, true)}</span>}
      </div>
      <div style={{ ...mutedSt, marginBottom: 10 }}>
        La Biblioteca del método usa estos cuatro datos para elegir qué entradas aplican a este cliente: una entrada de otra industria o de otro país no entra.
        {!ctx.complete && <b style={{ color: '#f59e0b' }}> Falta {ctx.missing.map(k => FIELDS.find(f => f.key === k)?.label.toLowerCase()).join(', ')}: elígelo para poder generar.</b>}
      </div>
      {error && <div style={{ ...warnSt, fontSize: 13 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
        {FIELDS.map(f => (
          <label key={f.key} style={{ display: 'block' }}>
            <span style={labelSt}>{f.label}</span>
            <select style={inputSt} disabled={busy} value={ctx.values[f.key] || ''} onChange={e => save(f.key, e.target.value)}>
              <option value="">{ctx.computed[f.key] ? '—' : 'Falta: elige uno'}</option>
              {f.options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <span style={{ ...mutedSt, fontSize: 11, display: 'block', marginTop: 3 }}>
              {ctx.values[f.key] ? (SOURCES[ctx.sources[f.key]] ? `Valor ${SOURCES[ctx.sources[f.key]]}` : '') : 'No se pudo calcular'}
              {ctx.sources[f.key] === 'ale' && ctx.computed[f.key] && (
                <> · calculado: {labelOfIn(f.options, ctx.computed[f.key])} <button type="button" className="action-btn" style={{ fontSize: 10, padding: '1px 6px' }} disabled={busy} onClick={() => save(f.key, null)}>Calculado</button></>
              )}
            </span>
          </label>
        ))}
      </div>
      {ctx.complete && !ctx.confirmedAt && (
        <div style={{ marginTop: 10 }}>
          <button className="action-btn" style={{ fontSize: 12 }} disabled={busy} onClick={() => save()}>✓ Confirmar estos datos</button>
        </div>
      )}
    </div>
  );
}
